/**
 * The ambience player (R35/D115) — the web renderer's ear: WebAudio beds,
 * equal-gain crossfades, and one shared clock of decoded buffers.
 *
 * @remarks
 * This is renderer-local machinery by contract (Calder's §8.2 split): the
 * pack declares WHAT plays and WHEN (`ambience`, `audibleWhen`-style
 * conditions); gain curves, crossfade time, and the decode strategy live
 * here and are never authored. WebAudio rather than `<audio>` because the
 * design needs two things `<audio>` cannot do (§6.4): two sources at once
 * with independent gain (the D99 crossfade), and sample-accurate seamless
 * loops (`AudioBufferSourceNode.loop` — the loop point is a data decision,
 * not an encoding one).
 *
 * The D99 discipline, sound-side: DECODE BEFORE THE FADE. `setTarget` awaits
 * the incoming buffer before starting the ramp, so a slow decode delays the
 * change rather than gapping it — and because every buffer is cached after
 * its first decode, later transitions are instant. Fades are equal-gain
 * linear ramps on the audio thread (`linearRampToValueAtTime`), so a busy
 * main thread revealing text cannot stutter them.
 *
 * Silence is a TARGET, not an absence (§6.2): `setTarget('silence')` fades
 * the current bed to nothing and remembers that the quiet is deliberate —
 * exactly the drop-out the crawlspace was promoted for. A later bed fades in
 * from that silence.
 *
 * Browsers gate audio behind a user gesture, so nothing here constructs an
 * `AudioContext` until `ensureStarted()` — the caller invokes it inside a
 * real interaction (the title screen's click). The module never touches
 * `window` at import time and takes its context factory and fetcher by
 * injection, so jsdom tests drive it with stubs and the isomorphic boundary
 * stays exactly where it was: this file is app shell, the one place browser
 * APIs belong.
 *
 * Fetching and decoding is `audio-buffers.ts`'s (D265): one cache for beds
 * and cues, with the AAC retry Safari needs and a prefetch for a game's cues.
 *
 * D264: the same player plays a turn's CUES — the one-shot sounds its lines
 * make — because they want everything the beds already have: the context a
 * gesture started, the decode cache and its AAC retry, the volume slider,
 * and the Sound switch. D265: a turn's cues are HEARD when the turn arrives
 * and PLAYED when the session has timed them to the reveal — each at the
 * moment its line begins, one line's cues one after another. A sound plays
 * over the bed; music lays the bed
 * down under itself (`MUSIC_DUCK_GAIN`, through a bus every bed passes
 * through, so a crossfade and a duck never fight over one node) and lifts it
 * when it ends, and a second piece of music fades out the first. Muted, a
 * turn's sounds are simply not heard — the lines that caption them are on
 * the page — rather than saved up for the moment sound comes back.
 *
 * @packageDocumentation
 */

import type { AmbienceTarget, CueView, TurnOutcome } from '@orpheus/api-contracts';

import { createAudioBufferCache } from './audio-buffers.js';

/** The subset of `AudioContext` the player drives (injectable for tests). */
export interface AmbienceAudioContext {
	createBufferSource: () => AudioBufferSourceNode;
	createGain: () => GainNode;
	readonly currentTime: number;
	decodeAudioData: (bytes: ArrayBuffer) => Promise<AudioBuffer>;
	readonly destination: AudioNode;
	resume: () => Promise<void>;
	suspend: () => Promise<void>;
}

/** Injectable dependencies — real browser pieces by default, stubs in tests. */
export interface AmbiencePlayerDependencies {
	/** Build the context (called once, inside the first user gesture). */
	readonly createContext: () => AmbienceAudioContext;
	/** Fetch one asset's compressed bytes. */
	readonly fetchBytes: (url: string) => Promise<ArrayBuffer>;
}

/** What the player should be playing: a bed by URL, or deliberate quiet — the contract's shape (D156), re-exported for the shell. */
export type { AmbienceTarget };

/** One playing bed: its source, its gain, and the key that identifies it. */
interface ActiveBed {
	readonly gainNode: GainNode;
	readonly key: string;
	readonly sourceNode: AudioBufferSourceNode;
}

/** The crossfade length — renderer-local by contract, never authored. */
export const CROSSFADE_SECONDS = 2;

/** D264: how far music lays the bed down — about 10 dB — renderer-local, never authored. */
export const MUSIC_DUCK_GAIN = 10 ** (-10 / 20);

/** D264: how long the bed takes to go down under music, and to come back, and how long one piece takes to give way to the next. */
export const MUSIC_RAMP_SECONDS = 1.5;

/**
 * The perceptual taper — the ONE seam between the player's stored SETTING
 * (the slider's handle position, 0..1) and the renderer's gain (D117). Cubic,
 * because loudness perception is roughly logarithmic and a linear slider
 * spends its whole travel in "too loud" without it. Every consumer of the
 * setting (the session driver, the title screen's wake) crosses here, so the
 * curve can change in exactly one place.
 *
 * @param setting - The stored slider setting, 0..1
 * @returns The gain to hand the player
 */
export const ambientGainFromSetting = (setting: number): number => setting ** 3;

/**
 * The layer's default GAIN — what the title theme plays at before the
 * session's driver takes over. Kept equal to the store's default SETTING
 * (0.22) through the same cubic taper the driver applies, so the landing and
 * the first room sound identical (D116/D117; settled by Mike's ear once the
 * slider's readout was honest). The generated beds master HOT, and Calder's
 * §3 rule is that these sit UNDER prose — the player's slider is the loud
 * option, not the rescue.
 */
export const DEFAULT_AMBIENT_VOLUME = ambientGainFromSetting(0.22);

/**
 * The ambience player — construct once per app, drive with `setTarget`.
 */
export interface AmbiencePlayer {
	/** Create (or resume) the context — call inside a user gesture. */
	readonly ensureStarted: () => void;
	/** D264/D265: hear the sounds a turn's lines made — held for `playHeard`; nothing while muted or before the gesture. */
	readonly hearTurn: (outcome: Pick<TurnOutcome, 'cues'>) => void;
	/**
	 * D265: play what was heard, each cue `atMsOf(cue)` milliseconds from now — the moment the reveal begins its line —
	 * and one line's cues one after another, in the order it says them.
	 */
	readonly playHeard: (atMsOf: (cue: CueView) => number) => void;
	/** D265: fetch a game's cues ahead of their first play, so the first one sounds on its line. Nothing while muted. */
	readonly prefetch: (urls: readonly string[]) => void;
	/** Mute or unmute the whole layer (the Sound toggle's lever). */
	readonly setEnabled: (isEnabled: boolean) => void;
	/** Crossfade to a bed or to silence; repeated identical targets no-op. */
	readonly setTarget: (target: AmbienceTarget) => void;
	/** Set the layer's master loudness, 0..1 (the volume slider's lever). */
	readonly setVolume: (volume: number) => void;
}

/**
 * Build an ambience player.
 *
 * @param dependencies - The context factory and fetcher (browser pieces by default)
 * @returns The player
 */
export const createAmbiencePlayer = (dependencies: AmbiencePlayerDependencies): AmbiencePlayer => {
	const buffers = createAudioBufferCache(dependencies.fetchBytes);
	// D265: a turn's cues, heard and waiting for the session to time them to their lines.
	let heardCues: readonly CueView[] = [];
	let audioContext: AmbienceAudioContext | undefined;
	let activeBed: ActiveBed | undefined;
	let currentTargetKey = 'silence';
	// D116: one master gain between every bed and the destination — the
	// volume slider's lever, so a fade-in-progress and the loudness setting
	// never fight over the same node.
	let masterGainNode: GainNode | undefined;
	let masterVolume = DEFAULT_AMBIENT_VOLUME;
	// D264: every bed passes through this bus on its way to the master, so music can lay the beds down as one.
	let bedBusNode: GainNode | undefined;
	let isEnabled = true;
	let playingMusic: { readonly gainNode: GainNode; readonly sourceNode: AudioBufferSourceNode } | undefined;
	// A target set before any gesture has started the context — applied the
	// moment `ensureStarted` runs, so the first bed needs no state change.
	let queuedTarget: AmbienceTarget | undefined;
	// A decode may still be in flight when the target changes again — the
	// generation stamp makes every stale arrival a no-op instead of a race.
	let targetGeneration = 0;

	const fadeOutActiveBed = (context: AmbienceAudioContext): void => {
		if (activeBed === undefined) {
			return;
		}

		const departingBed = activeBed;
		const fadeEnd = context.currentTime + CROSSFADE_SECONDS;

		departingBed.gainNode.gain.setValueAtTime(departingBed.gainNode.gain.value, context.currentTime);
		departingBed.gainNode.gain.linearRampToValueAtTime(0, fadeEnd);
		// Stop just past the ramp's end — the source is done, not paused.
		departingBed.sourceNode.stop(fadeEnd + 0.05);
		activeBed = undefined;
	};

	const fadeInBed = (context: AmbienceAudioContext, key: string, buffer: AudioBuffer): void => {
		const sourceNode = context.createBufferSource();
		const gainNode = context.createGain();

		sourceNode.buffer = buffer;
		sourceNode.loop = true;
		sourceNode.connect(gainNode);
		/* v8 ignore next 2 -- fadeInBed runs only after ensureStarted built the
		 * bus; the fallback is a type-level obligation. */
		gainNode.connect(bedBusNode ?? context.destination);
		gainNode.gain.setValueAtTime(0, context.currentTime);
		gainNode.gain.linearRampToValueAtTime(1, context.currentTime + CROSSFADE_SECONDS);
		sourceNode.start();

		activeBed = { gainNode, key, sourceNode };
	};

	/** D264: move the bed bus to `gain` over the music ramp, starting at `at` on the audio clock. */
	const rampBeds = (gain: number, at: number): void => {
		/* v8 ignore next 3 -- cues are scheduled only after ensureStarted built the
		 * bus; the guard is a type-level obligation. */
		if (bedBusNode === undefined) {
			return;
		}

		bedBusNode.gain.setValueAtTime(bedBusNode.gain.value, at);
		bedBusNode.gain.linearRampToValueAtTime(gain, at + MUSIC_RAMP_SECONDS);
	};

	/** D264: one cue, from its start at `startAt`; music lays the beds down under itself and lifts them when it ends. */
	const scheduleCue = (context: AmbienceAudioContext, cue: CueView, buffer: AudioBuffer, startAt: number): void => {
		const sourceNode = context.createBufferSource();
		const gainNode = context.createGain();

		sourceNode.buffer = buffer;
		sourceNode.connect(gainNode);
		/* v8 ignore next 2 -- a cue is scheduled only after ensureStarted built the
		 * master; the fallback is a type-level obligation. */
		gainNode.connect(masterGainNode ?? context.destination);
		sourceNode.start(startAt);

		if (cue.layer === 'sound') {
			return;
		}

		// A second piece of music takes the first one's place: the first fades as the second begins.
		if (playingMusic !== undefined) {
			playingMusic.gainNode.gain.setValueAtTime(playingMusic.gainNode.gain.value, startAt);
			playingMusic.gainNode.gain.linearRampToValueAtTime(0, startAt + MUSIC_RAMP_SECONDS);
			playingMusic.sourceNode.stop(startAt + MUSIC_RAMP_SECONDS + 0.05);
		}

		const music = { gainNode, sourceNode };

		playingMusic = music;
		rampBeds(MUSIC_DUCK_GAIN, startAt);
		sourceNode.addEventListener('ended', () => {
			// A piece that gave way to the next one leaves the beds where the next one put them.
			if (playingMusic === music) {
				playingMusic = undefined;
				rampBeds(1, context.currentTime);
			}
		});
	};

	const applyTarget = (target: AmbienceTarget): void => {
		const targetKey = target === 'silence' ? 'silence' : target.key;

		if (targetKey === currentTargetKey) {
			return;
		}

		currentTargetKey = targetKey;
		targetGeneration += 1;

		const context = audioContext;

		/* v8 ignore next 3 -- applyTarget is reached only via setTarget's queue
		 * arm or ensureStarted, both of which guarantee a context; the guard is
		 * a type-level obligation. */
		if (context === undefined) {
			return;
		}

		if (target === 'silence') {
			fadeOutActiveBed(context);

			return;
		}

		const requestGeneration = targetGeneration;

		// D99's rule, sound-side: decode BEFORE the fade — the outgoing bed
		// keeps playing until the incoming one is ready to breathe in.
		void buffers
			.bufferFor(context, target.url)
			.then((buffer) => {
				if (requestGeneration !== targetGeneration) {
					return;
				}

				fadeOutActiveBed(context);
				fadeInBed(context, target.key, buffer);
			})
			.catch(() => {
				// A bed that cannot be fetched or decoded stays silent — the game
				// is whole as text, and a 404 must not break the session.
			});
	};

	return {
		ensureStarted: () => {
			if (audioContext === undefined) {
				// An environment without WebAudio (jsdom, an ancient embedder)
				// simply has no ambience layer — the game is whole as text, so
				// the player degrades to a no-op rather than crashing the shell.
				try {
					audioContext = dependencies.createContext();
				} catch {
					return;
				}
			}

			// A context constructed outside a gesture boots suspended in some
			// browsers; resuming inside one is always legal and often required.
			void audioContext.resume();

			// D116: the master gain, built once with the context, at whatever
			// loudness was asked for before the gesture arrived — and (D264) the
			// beds' bus in front of it.
			if (masterGainNode === undefined) {
				masterGainNode = audioContext.createGain();
				masterGainNode.gain.setValueAtTime(masterVolume, audioContext.currentTime);
				masterGainNode.connect(audioContext.destination);
				bedBusNode = audioContext.createGain();
				bedBusNode.connect(masterGainNode);
			}

			if (queuedTarget !== undefined) {
				const target = queuedTarget;

				queuedTarget = undefined;
				applyTarget(target);
			}
		},

		hearTurn: ({ cues }: Pick<TurnOutcome, 'cues'>) => {
			// Muted, a turn's sounds are not heard rather than saved up for the moment sound comes back.
			if (audioContext !== undefined && isEnabled && cues !== undefined) {
				heardCues = [...heardCues, ...cues];
			}
		},

		playHeard: (atMsOf: (cue: CueView) => number) => {
			const context = audioContext;
			const cues = heardCues;

			heardCues = [];

			if (context === undefined || !isEnabled || cues.length === 0) {
				return;
			}

			// Times are from NOW — the render that began the reveal — however long the decode takes.
			const heardAt = context.currentTime;

			void Promise.all(cues.map(async (cue) => ({ atMs: atMsOf(cue), buffer: await buffers.bufferFor(context, cue.url), cue })))
				.then((decoded) => {
					let previous: { readonly atMs: number; readonly endsAt: number } | undefined;

					for (const { atMs, buffer, cue } of decoded) {
						// With its line; after the cue before it when both are the same line's; never in the past.
						const startAt = Math.max(heardAt + atMs / 1000, context.currentTime, previous?.atMs === atMs ? previous.endsAt : 0);

						scheduleCue(context, cue, buffer, startAt);
						previous = { atMs, endsAt: startAt + buffer.duration };
					}
				})
				.catch(() => {
					// A cue that cannot be fetched or decoded stays silent — its line
					// still says the sound, and a 404 must not break the session.
				});
		},

		prefetch: (urls: readonly string[]) => {
			if (isEnabled) {
				for (const url of urls) {
					buffers.prefetch(url);
				}
			}
		},

		setEnabled: (nextIsEnabled: boolean) => {
			isEnabled = nextIsEnabled;

			if (audioContext === undefined) {
				return;
			}

			// One suspend/resume IS the whole mute control (§6.4) — the graph
			// keeps its place, and the bed is exactly where it was on return.
			void (nextIsEnabled ? audioContext.resume() : audioContext.suspend());
		},

		setTarget: (target: AmbienceTarget) => {
			if (audioContext === undefined) {
				// No gesture has started the context yet — remember the newest
				// ask; `ensureStarted` applies it, so the first bed needs no
				// state change to begin.
				queuedTarget = target;

				return;
			}

			applyTarget(target);
		},

		setVolume: (volume: number) => {
			// Clamped so a wild store value can never blast or invert the graph.
			masterVolume = Math.min(1, Math.max(0, volume));

			if (audioContext !== undefined && masterGainNode !== undefined) {
				// A short ramp rather than a step — a dragged slider must not
				// crackle (the zipper-noise classic).
				masterGainNode.gain.setValueAtTime(masterGainNode.gain.value, audioContext.currentTime);
				masterGainNode.gain.linearRampToValueAtTime(masterVolume, audioContext.currentTime + 0.05);
			}
		},
	};
};

/**
 * The browser's real dependencies — split out so tests never touch them.
 *
 * @returns Dependencies wired to `AudioContext` and `fetch`
 */
export const browserAmbienceDependencies = (): AmbiencePlayerDependencies => ({
	createContext: () => new AudioContext(),
	fetchBytes: async (url: string) => fetch(url).then(async (response) => response.arrayBuffer()),
});
